// Documents Service for East End Prep Sports
// Handles document management API calls for both admin and public pages

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class DocumentsService {
  // Get authentication headers
  static getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  // Get all active documents (public)
  static async getActiveDocuments() {
    try {
      const response = await fetch(`${API_BASE_URL}/documents`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch documents: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Get all documents including inactive (admin)
  static async getAllDocuments() {
    try {
      const headers = {
        ...this.getAuthHeaders(),
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      };
      
      const response = await fetch(`${API_BASE_URL}/documents/admin`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch all documents: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Upload new document
  static async uploadDocument(formData) {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/documents`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upload document: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Update document
  static async updateDocument(id, data) {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update document: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Delete document
  static async deleteDocument(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete document: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Download document
  // Download document - Completely rewritten for better handling
  // Simple download function that triggers direct download from S3
  static async downloadDocument(id, filename = null) {
    try {
      // Simply redirect to the download endpoint - let the backend handle S3 redirect
      const downloadUrl = `${API_BASE_URL}/documents/${id}/download`;
      
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank'; // Open in new tab to handle redirects properly
      if (filename) {
        link.download = filename;
      }
      link.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      return { success: true };
    } catch (error) {
      console.error('Download error:', error);
      throw new Error('Failed to download document. Please try again.');
    }
  }

  // Get download URL for direct linking
  static getDownloadUrl(id) {
    return `${API_BASE_URL}/documents/${id}/download`;
  }
}

export default DocumentsService;